import { useRef } from "react";
import { format, parseISO, addDays, isToday, isTomorrow, isYesterday } from "date-fns";
import { ChevronLeft, ChevronRight, ChevronDown, Undo2 } from "lucide-react";
import { cn } from "@/lib/cn";

function dateISO(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

interface Props {
  value: string; // YYYY-MM-DD
  onChange: (newDate: string) => void;
}

/**
 * DateNavigator (BOARD-HD, Seth 2026-09-01: "instead of the separate calendar
 * picker — if we click 'today' instead can I bring up a calendar?") — the date
 * itself IS the picker now. Click the center of the stepper and the native
 * calendar pops right under it; the standalone date input is gone. Arrows
 * still step a day at a time, and a "Today" jump appears whenever you're
 * looking at any other day.
 */
export function DateNavigator({ value, onChange }: Props) {
  const date = parseISO(value);
  const dateRef = useRef<HTMLInputElement | null>(null);

  const prev = () => onChange(dateISO(addDays(date, -1)));
  const next = () => onChange(dateISO(addDays(date, 1)));
  const today = () => onChange(dateISO(new Date()));
  const openPicker = () => {
    const el = dateRef.current;
    if (!el) return;
    // Same proven pattern as the mobile Manager view (2026-08-31): native
    // showPicker where supported, focus+click fallback elsewhere.
    try {
      (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
    } catch {
      el.focus();
      el.click();
    }
  };

  let relativeLabel: string | null = null;
  if (isToday(date)) relativeLabel = "Today";
  else if (isTomorrow(date)) relativeLabel = "Tomorrow";
  else if (isYesterday(date)) relativeLabel = "Yesterday";

  const dayLabel = format(date, "EEEE");
  const dateLabel = format(date, "MMMM d, yyyy");

  return (
    <div className="flex items-center gap-2">
      {/* One connected stepper — arrows cycle the date, the DATE opens a calendar. */}
      <div className="inline-flex items-stretch overflow-hidden rounded-full border border-hairline bg-white shadow-e1">
        <button
          type="button"
          onClick={prev}
          aria-label="Previous day"
          className="flex items-center border-r border-hairline pl-3 pr-2.5 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={openPicker}
            title="Pick a date from the calendar"
            className="group flex min-w-[176px] flex-col items-center justify-center px-3 py-1 leading-tight transition-colors hover:bg-slate-50"
          >
            <span className="flex items-center gap-1.5">
              {relativeLabel && (
                <span
                  className={cn(
                    "text-[11px] font-bold uppercase tracking-wider",
                    isToday(date) ? "text-brand-600" : "text-slate-500",
                  )}
                >
                  {relativeLabel}
                </span>
              )}
              <span className="text-[14.5px] font-bold tracking-tight text-slate-900">
                {dayLabel}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-slate-500" />
            </span>
            <span className="text-xs text-slate-500">{dateLabel}</span>
          </button>
          {/* Invisible input anchored under the label — the native calendar
              opens right below the date, exactly where the click happened. */}
          <input
            ref={dateRef}
            type="date"
            value={value}
            onChange={(e) => {
              if (e.target.value) onChange(e.target.value);
            }}
            tabIndex={-1}
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-0 p-0 opacity-0"
          />
        </div>

        <button
          type="button"
          onClick={next}
          aria-label="Next day"
          className="flex items-center border-l border-hairline pl-2.5 pr-3 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {!isToday(date) && (
        <button
          type="button"
          onClick={today}
          title="Jump back to today's board"
          className="inline-flex h-8 items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-3 text-[12.5px] font-semibold text-brand-700 transition-colors hover:bg-brand-100"
        >
          <Undo2 className="h-3.5 w-3.5" />
          Today
        </button>
      )}
    </div>
  );
}
